import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import PositiveRecruitment from "./pages/PositiveRecruitment";
import CursorGlow from "./components/CursorGlow";
import Hreflang from "./components/Hreflang";
import AnalyticsTracker from "./components/Analytics";
import ScrollToTop from "./components/ScrollToTop";
import GlobalSideRail from "./components/GlobalSideRail";
import SmoothAnchors from "./components/SmoothAnchors";
// Dev-only SEO diagnostics; tree-shaken out of production builds.
const SeoDevPanel = import.meta.env.DEV ? lazy(() => import("./components/SeoDevPanel")) : null;
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "./crm/lib/auth-context";
import { AuthGuard } from "./crm/components/AuthGuard";

// Strona landing ładuje się od razu; reszta (CRM, strony poboczne) leniwie,
// dzięki czemu wejście na "/" nie ściąga kodu panelu CRM (recharts, xlsx itd.).
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PolishQaTest = lazy(() => import("./pages/PolishQaTest"));
const ClientPortal = lazy(() => import("./pages/ClientPortal"));
const SalaryGuidePoland = lazy(() => import("./pages/SalaryGuidePoland"));
const AuthorWojciechKluk = lazy(() => import("./pages/AuthorWojciechKluk"));
const InsightsHub = lazy(() => import("./pages/InsightsHub"));
const InsightArticle = lazy(() => import("./pages/InsightArticle"));
const ItRpoPoland = lazy(() => import("./pages/ItRpoPoland"));
const Uslugi = lazy(() => import("./pages/Uslugi"));
const DlaKandydatow = lazy(() => import("./pages/DlaKandydatow"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const ONas = lazy(() => import("./pages/ONas"));

const CrmLayout = lazy(() => import("./crm/components/CrmLayout"));
const Login = lazy(() => import("./crm/pages/Login"));
const ResetPassword = lazy(() => import("./crm/pages/ResetPassword"));
const Dashboard = lazy(() => import("./crm/pages/Dashboard"));
const CandidatesList = lazy(() => import("./crm/pages/CandidatesList"));
const CandidateProfile = lazy(() => import("./crm/pages/CandidateProfile"));
const ImportPage = lazy(() => import("./crm/pages/Import"));
const TasksPage = lazy(() => import("./crm/pages/Tasks"));
const LinkedInIngest = lazy(() => import("./crm/pages/LinkedInIngest"));
const TalentPoolsPage = lazy(() => import("./crm/pages/TalentPools"));
const AIReplyCenter = lazy(() => import("./crm/pages/AIReplyCenter"));
const SettingsPage = lazy(() => import("./crm/pages/Settings"));
const Campaigns = lazy(() => import("./crm/pages/Campaigns"));
const Jobs = lazy(() => import("./crm/pages/Jobs"));
const JobDetail = lazy(() => import("./crm/pages/JobDetail"));
const Analytics = lazy(() => import("./crm/pages/Analytics"));
const OnePager = lazy(() => import("./crm/pages/OnePager"));

const App = () => (
  <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
    <AuthProvider>
      <ScrollToTop />
      <SmoothAnchors />
      <Hreflang />
      <AnalyticsTracker />
      <CursorGlow />
      <GlobalSideRail />
      {SeoDevPanel && (
        <Suspense fallback={null}>
          <SeoDevPanel />
        </Suspense>
      )}
      <Toaster />
      <Suspense fallback={<div className="min-h-screen bg-[#050706]" aria-hidden />}>
        <Routes>
          <Route path="/" element={<PositiveRecruitment />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/jakosc" element={<PolishQaTest />} />
          <Route path="/o-nas" element={<ONas />} />
          <Route path="/uslugi" element={<Uslugi />} />
          <Route path="/dla-kandydatow" element={<DlaKandydatow />} />
          <Route path="/kontakt" element={<Kontakt />} />
          <Route path="/services" element={<Navigate to="/uslugi" replace />} />
          <Route path="/candidates" element={<Navigate to="/dla-kandydatow" replace />} />
          <Route path="/contact" element={<Navigate to="/kontakt" replace />} />
          <Route path="/about-us" element={<Navigate to="/o-nas" replace />} />
          {/* Legacy WordPress client-logo pages still indexed by Google. */}
          <Route path="/gs-logo-slider/*" element={<Navigate to="/#customers" replace />} />
          <Route path="/o-nas/wojciech-kluk" element={<AuthorWojciechKluk />} />

          <Route path="/about/wojciech-kluk" element={<AuthorWojciechKluk />} />
          <Route path="/insights" element={<InsightsHub />} />
          <Route path="/insights/it-salary-guide-poland" element={<SalaryGuidePoland />} />
          <Route path="/insights/:slug" element={<InsightArticle />} />
          <Route path="/services/it-rpo-poland" element={<ItRpoPoland />} />
          <Route path="/crm/login" element={<Login />} />
          <Route path="/crm/reset-password" element={<ResetPassword />} />
          <Route path="/portal/:slug" element={<ClientPortal />} />


          <Route path="/crm" element={<AuthGuard><CrmLayout /></AuthGuard>}>
            <Route index element={<Dashboard />} />
            <Route path="candidates" element={<CandidatesList />} />
            <Route path="candidates/:id" element={<CandidateProfile />} />
            <Route path="candidates/:id/one-pager" element={<OnePager />} />
            <Route path="jobs" element={<Jobs />} />
            <Route path="jobs/:id" element={<JobDetail />} />
            <Route path="tasks" element={<TasksPage />} />
            <Route path="import" element={<ImportPage />} />
            <Route path="linkedin" element={<LinkedInIngest />} />
            <Route path="pools" element={<TalentPoolsPage />} />
            <Route path="ai" element={<AIReplyCenter />} />
            <Route path="campaigns" element={<Campaigns />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="settings" element={<SettingsPage />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
